"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Download, FilterX, KeyRound, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Alert, Button, EmptyState, ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, Modal, Skeleton, SkeletonTableRow, ToolbarIconButton, WorkspacePage } from "@/components/ui-system";
import { TableHeaderSearch, ThemedSelect } from "@/components/ui-system/select";
import {
  createExternalDeviceApiKey,
  deleteExternalDeviceApiKey,
  listExternalDeviceApiKeys,
  type ExternalDeviceApiKeyCreatedDto,
} from "@/lib/api/endpoints/external-data-access";
import { cn } from "@/lib/utils/cn";
import { useDevice } from "@/modules/devices/hooks";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const EXTERNAL_API_BASE_URL = "https://datalogger.swsagent.com";

const EXTERNAL_SCOPES = [
  { key: "device_readings:read", label: "Readings", hint: "Raw paginated telemetry rows" },
  { key: "device_alerts:read", label: "Alerts", hint: "Device alert history" },
  { key: "device_lifecycle:read", label: "Lifecycle", hint: "Installation, calibration, and warranty dates" },
] as const;

function formatDateTime(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function downloadExternalAccessDoc(deviceId: string, deviceLabel: string) {
  const escapeHtml = (value: string) =>
    value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");

  const safeDeviceId = escapeHtml(deviceId);
  const safeDeviceLabel = escapeHtml(deviceLabel);
  const safeTitle = escapeHtml(`${deviceLabel} external data access`);
  const safeApiBase = escapeHtml(EXTERNAL_API_BASE_URL);
  const fileNameBase = `${deviceLabel || "device"}-external-access`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "device-external-access";
  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>
      :root { color-scheme: light; }
      body {
        font-family: Arial, sans-serif;
        margin: 32px;
        color: #0f172a;
        line-height: 1.5;
      }
      h1 { font-size: 24px; margin: 0 0 8px; }
      p, li { font-size: 13px; }
      .muted { color: #475569; }
      .card {
        border: 1px solid #cbd5e1;
        border-radius: 12px;
        padding: 16px;
        margin-top: 12px;
        background: #ffffff;
      }
      code {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 1px 6px;
        font-family: Consolas, monospace;
        font-size: 12px;
      }
      .intro {
        border-left: 4px solid #2563eb;
        background: #eff6ff;
        border-radius: 10px;
        padding: 12px 14px;
        margin-top: 16px;
      }
      ul { margin: 8px 0 0 18px; padding: 0; }
      .endpoint { font-size: 14px; font-weight: 700; margin-bottom: 8px; }
      .mono-block {
        white-space: pre-wrap;
        font-family: Consolas, monospace;
        font-size: 12px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 12px;
      }
    </style>
  </head>
  <body>
    <h1>External data access</h1>
    <p class="muted">Device: ${safeDeviceLabel} (${safeDeviceId})</p>
    <div class="intro">
      <strong>Authentication</strong>
      <p class="muted">Use either the request header or include the key directly in the URL path.</p>
      <div class="mono-block">Header option:
X-Api-Key: your-key

URL option:
/external/devices/${safeDeviceId}/your-key/...</div>
    </div>

    <div class="card">
      <div class="endpoint">GET ${safeApiBase}/external/devices/${safeDeviceId}/readings</div>
      <p>Returns raw paginated telemetry rows for this device.</p>
      <p><strong>Default behavior</strong></p>
      <ul>
        <li>Latest records first</li>
        <li><code>page=1</code></li>
        <li><code>per_page=20</code></li>
      </ul>
      <p><strong>Allowed query params</strong></p>
      <ul>
        <li><code>page</code>: page number, minimum 1</li>
        <li><code>per_page</code>: 20 to 100</li>
        <li><code>start</code>: ISO datetime lower bound</li>
        <li><code>end</code>: ISO datetime upper bound</li>
        <li><code>channels</code>: one or more channel names</li>
      </ul>
      <div class="mono-block">Example:
Header auth:
GET ${safeApiBase}/external/devices/${safeDeviceId}/readings?page=1&amp;per_page=20

URL auth:
GET ${safeApiBase}/external/devices/${safeDeviceId}/your-key/readings?page=1&amp;per_page=20

Header auth with filters:
GET ${safeApiBase}/external/devices/${safeDeviceId}/readings?start=2026-06-01T00:00:00Z&amp;end=2026-06-15T23:59:59Z
GET ${safeApiBase}/external/devices/${safeDeviceId}/readings?channels=temperature&amp;channels=humidity</div>
    </div>

    <div class="card">
      <div class="endpoint">GET ${safeApiBase}/external/devices/${safeDeviceId}/alerts</div>
      <p>Returns paginated alert history for this device.</p>
      <p><strong>Default behavior</strong></p>
      <ul>
        <li>Latest alerts first</li>
        <li><code>page=1</code></li>
        <li><code>per_page=20</code></li>
      </ul>
      <p><strong>Allowed query params</strong></p>
      <ul>
        <li><code>page</code>: page number, minimum 1</li>
        <li><code>per_page</code>: 20 to 100</li>
      </ul>
      <div class="mono-block">Example:
Header auth:
GET ${safeApiBase}/external/devices/${safeDeviceId}/alerts?page=1&amp;per_page=20

URL auth:
GET ${safeApiBase}/external/devices/${safeDeviceId}/your-key/alerts?page=1&amp;per_page=20</div>
    </div>

    <div class="card">
      <div class="endpoint">GET ${safeApiBase}/external/devices/${safeDeviceId}/lifecycle</div>
      <p>Returns lifecycle information for this device.</p>
      <p><strong>Fields returned</strong></p>
      <ul>
        <li><code>installation_date</code></li>
        <li><code>next_calibration</code></li>
        <li><code>warranty_end</code></li>
      </ul>
      <p><strong>Allowed query params</strong></p>
      <ul>
        <li>None</li>
      </ul>
      <div class="mono-block">Example:
Header auth:
GET ${safeApiBase}/external/devices/${safeDeviceId}/lifecycle

URL auth:
GET ${safeApiBase}/external/devices/${safeDeviceId}/your-key/lifecycle</div>
    </div>
  </body>
</html>`;
  const printWindow = window.open("", "_blank", "width=980,height=900");
  if (!printWindow) {
    return;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.setTimeout(() => {
      printWindow.document.title = `${fileNameBase}.pdf`;
      printWindow.print();
    }, 150);
  };
}

export function DeviceExternalAccessPage({ deviceId }: { deviceId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogs = useAppDialogs();
  const deviceQuery = useDevice(deviceId);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>(() => EXTERNAL_SCOPES.map((scope) => scope.key));
  const [createdKey, setCreatedKey] = useState<ExternalDeviceApiKeyCreatedDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryKey = useMemo(() => ["devices", "external-api-keys", deviceId], [deviceId]);

  const listQuery = useQuery({
    queryKey,
    queryFn: () => listExternalDeviceApiKeys(deviceId, { search: debouncedSearch, page: page + 1, pageSize }),
    enabled: Boolean(deviceId),
    staleTime: 15_000,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const createMutation = useMutation({
    mutationFn: (body: { name: string; scopes: string[] }) => createExternalDeviceApiKey(deviceId, body),
    onSuccess: (data) => {
      setCreatedKey(data);
      setName("");
      setError(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Could not create external API key.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteExternalDeviceApiKey(deviceId, keyId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Could not delete API key.");
    },
  });

  const canSubmit = name.trim().length > 0 && selectedScopes.length > 0 && !createMutation.isPending;
  const device = deviceQuery.data;
  const deviceLabel = device?.name?.trim() || device?.device_uid || deviceId;
  const keys = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageLabel = total === 0 ? "Showing 0 of 0" : `Showing ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, total)} of ${total}`;

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      await dialogs.copy({
        title: "Copy API key",
        description: "Clipboard access is unavailable. Copy the API key manually below.",
        label: "API key",
        value,
      });
    }
  };

  function resetCreateModal() {
    setCreateOpen(false);
    setName("");
    setSelectedScopes(EXTERNAL_SCOPES.map((scope) => scope.key));
    setCreatedKey(null);
    setError(null);
  }

  if (deviceQuery.isLoading) {
    return (
      <WorkspacePage className="max-w-[96rem] pt-0">
        <div className="space-y-ds-6">
          <Skeleton className="h-24 rounded-control" />
          <Skeleton className="h-16 rounded-control" />
          <Skeleton className="h-[24rem] rounded-control" />
        </div>
      </WorkspacePage>
    );
  }

  if (deviceQuery.isError || !device) {
    return (
      <WorkspacePage className="pt-0">
        <Alert tone="error" title="Device unavailable" description={`Unable to load device ${deviceId}. Check connectivity, credentials, and workspace scope.`} />
      </WorkspacePage>
    );
  }

  return (
    <WorkspacePage className="max-w-[96rem] pt-0">
      <ListSurface>
        <ListSurfaceHeader
          title="External access keys"
          description="Use device-bound keys for external integrations. Deleting a key revokes access immediately."
          meta={<ListCount total={total} filtered={total} noun={total === 1 ? "key" : "keys"} />}
          controls={
            <ListToolbar>
              <ToolbarIconButton icon={ArrowLeft} label="Back to devices" onClick={() => navigate("/devices")} />
              <ListToolbarGroup grow className="max-w-xl">
                <TableHeaderSearch
                  value={search}
                  onChange={setSearch}
                  placeholder="Search key name, scope, creator, or prefix..."
                  className="w-full"
                />
              </ListToolbarGroup>
              {search.trim() ? (
                <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
              ) : null}
              <ToolbarIconButton icon={Download} label="Download documentation" onClick={() => downloadExternalAccessDoc(deviceId, deviceLabel)} />
              <ToolbarIconButton icon={Plus} label="Add key" variant="primary" onClick={() => setCreateOpen(true)} />
            </ListToolbar>
          }
        />

        {listQuery.isLoading ? (
          <ListSurfaceBody>
            <div className="p-ds-4" aria-label="Loading API keys">
              {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
            </div>
          </ListSurfaceBody>
        ) : listQuery.isError ? (
          <ListSurfaceBody>
            <Alert className="m-ds-4" tone="error" title="External access unavailable" description={listQuery.error instanceof Error ? listQuery.error.message : "Could not load external API keys."} />
          </ListSurfaceBody>
        ) : total === 0 ? (
          <ListSurfaceBody>
            <div className="p-ds-6">
              <EmptyState
                icon={KeyRound}
                title="No API keys yet"
                description="Create a device-bound key to enable external readings, alerts, or lifecycle access."
                action={
                  <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
                    Add key
                  </Button>
                }
              />
            </div>
          </ListSurfaceBody>
        ) : (
          <ListSurfaceBody className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-border/60 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                  <th className="px-ds-4 py-ds-3">Name</th>
                  <th className="px-ds-4 py-ds-3">Scopes</th>
                  <th className="px-ds-4 py-ds-3">Created by</th>
                  <th className="px-ds-4 py-ds-3">Created</th>
                  <th className="px-ds-4 py-ds-3">Last used</th>
                  <th className="px-ds-4 py-ds-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((item, index) => (
                  <tr key={item.id} className={cn(index % 2 === 1 && "bg-primary/5", "border-b border-border/35 last:border-b-0")}>
                    <td className="px-ds-4 py-ds-4 align-top">
                      <div className="min-w-0">
                        <div className="truncate text-caption font-semibold text-foreground">{item.name || "Untitled key"}</div>
                        <div className="mt-1 text-micro text-muted">
                          Prefix: <code className="rounded bg-muted-bg/30 px-1 py-0.5 text-foreground">{item.key_prefix || "--"}</code>
                        </div>
                      </div>
                    </td>
                    <td className="px-ds-4 py-ds-4 align-top">
                      <div className="flex max-w-[24rem] flex-wrap gap-ds-2">
                        {item.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="rounded-full border border-border/40 bg-muted-bg/20 px-ds-2 py-1 text-micro text-foreground"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-ds-4 py-ds-4 align-top text-caption text-foreground">
                      {item.created_by_name?.trim() || item.created_by_email || "Unknown"}
                    </td>
                    <td className="px-ds-4 py-ds-4 align-top text-caption text-muted">{formatDateTime(item.created_at)}</td>
                    <td className="px-ds-4 py-ds-4 align-top text-caption text-muted">{formatDateTime(item.last_used_at)}</td>
                    <td className="px-ds-4 py-ds-4 align-top text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="!p-2 text-error"
                        onClick={() => {
                          void (async () => {
                            const label = item.name?.trim() || "this API key";
                            const confirmed = await dialogs.confirm({
                              title: `Delete ${label}?`,
                              description: "This revokes external access immediately.",
                              confirmLabel: "Delete key",
                              tone: "danger",
                            });
                            if (!confirmed) {
                              return;
                            }
                            void deleteMutation.mutateAsync(item.id);
                          })();
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ListSurfaceBody>
        )}
          <ListSurfaceFooter
            left={
              <label className="flex items-center gap-ds-2 md:justify-self-start">
                <span className="text-caption text-muted">Rows per page</span>
                <ThemedSelect
                  value={String(pageSize)}
                  ariaLabel="Rows per page"
                  className="w-[5.5rem]"
                  triggerClassName="h-10 border-border bg-card hover:border-primary/20"
                  options={PAGE_SIZE_OPTIONS.map((option) => ({
                    value: String(option),
                    label: String(option),
                  }))}
                  onChange={(nextValue) => {
                    setPage(0);
                    setPageSize(Number(nextValue));
                  }}
                />
              </label>
            }
            right={
              <>
                <span className="text-caption text-muted">{pageLabel}</span>
                <div className="flex items-center gap-ds-2 md:justify-self-end">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                    disabled={page <= 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    Previous
                  </button>
                  <span className="text-caption tabular-nums text-muted">
                    Page {Math.min(page + 1, pageCount)} / {pageCount}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                    disabled={page + 1 >= pageCount}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </>
            }
          />
      </ListSurface>

      <Modal
        open={createOpen}
        onClose={resetCreateModal}
        title={`Add external access key for ${deviceLabel}`}
        description="Create a one-device key for external telemetry access. API usage guidance is included below."
        size="lg"
        footer={
          <div className="flex justify-end gap-ds-2">
            <Button type="button" variant="secondary" onClick={resetCreateModal}>
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                setError(null);
                void createMutation.mutateAsync({ name: name.trim(), scopes: selectedScopes });
              }}
              disabled={!canSubmit}
            >
              {createMutation.isPending ? "Creating..." : "Create key"}
            </Button>
          </div>
        }
      >
        <div className="space-y-ds-5">
          {createdKey ? (
              <div className="rounded-control border border-success/25 bg-success/10 p-ds-4">
              <div className="flex flex-wrap items-start justify-between gap-ds-3">
                <div>
                  <p className="text-micro font-semibold uppercase tracking-wider text-success/80">Copy now</p>
                  <p className="mt-1 text-caption text-foreground">
                    This key is shown once. Store it before closing the modal.
                  </p>
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={() => void copyText(createdKey.key)}>
                  <Copy className="h-4 w-4" aria-hidden />
                  Copy key
                </Button>
              </div>
              <div className="mt-ds-3 overflow-x-auto rounded-control border border-success/20 bg-surface-primary px-ds-3 py-ds-3 font-mono text-micro text-foreground">
                {createdKey.key}
              </div>
            </div>
          ) : null}

          {error ? (
            <Alert tone="error" title="Could not create access key" description={error} />
          ) : null}

          <section className="space-y-ds-4">
            <label className="block space-y-ds-1">
              <span className="text-micro font-semibold uppercase tracking-wider text-muted">Key name</span>
              <input
                className="h-11 w-full rounded-control border border-card-border bg-surface-primary px-ds-3 text-caption text-foreground focus:border-primary focus:outline-none"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Vendor export key"
              />
            </label>

            <div className="space-y-ds-2">
              <p className="text-micro font-semibold uppercase tracking-wider text-muted">Scopes</p>
              {EXTERNAL_SCOPES.map((scope) => {
                const checked = selectedScopes.includes(scope.key);
                return (
                  <label
                    key={scope.key}
                    className="flex items-start gap-ds-3 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-3"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                      checked={checked}
                      onChange={(event) => {
                        setSelectedScopes((current) =>
                          event.target.checked
                            ? [...current, scope.key]
                            : current.filter((value) => value !== scope.key),
                        );
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block text-caption font-medium text-foreground">{scope.label}</span>
                      <span className="block text-micro text-muted">{scope.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="rounded-control border border-card-border bg-surface-inset p-ds-4">
            <div className="flex items-center gap-ds-2 text-caption font-medium text-foreground">
              <KeyRound className="h-4 w-4 text-primary" aria-hidden />
              API usage
            </div>
            <div className="mt-ds-3 space-y-ds-3 text-micro text-muted">
              <p>
                Header auth: <code className="rounded bg-muted-bg/40 px-1 py-0.5 text-foreground">X-Api-Key: your-key</code>
              </p>
              <p>
                URL auth: <code className="rounded bg-muted-bg/40 px-1 py-0.5 text-foreground">/external/devices/{device.id}/your-key/...</code>
              </p>
              <div className="rounded-control border border-card-border bg-surface-primary p-ds-3 font-mono text-micro text-foreground">
                GET {EXTERNAL_API_BASE_URL}/external/devices/{device.id}/readings?page=1&amp;per_page=20
                <br />
                GET {EXTERNAL_API_BASE_URL}/external/devices/{device.id}/your-key/readings?page=1&amp;per_page=20
                <br />
                GET {EXTERNAL_API_BASE_URL}/external/devices/{device.id}/alerts?page=1&amp;per_page=20
                <br />
                GET {EXTERNAL_API_BASE_URL}/external/devices/{device.id}/your-key/alerts?page=1&amp;per_page=20
                <br />
                GET {EXTERNAL_API_BASE_URL}/external/devices/{device.id}/lifecycle
                <br />
                GET {EXTERNAL_API_BASE_URL}/external/devices/{device.id}/your-key/lifecycle
              </div>
            </div>
          </section>
        </div>
      </Modal>
    </WorkspacePage>
  );
}
